<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("reportNumMeeting") }}</h4>
        <div>
          <b-button variant="outline-success" :disabled="!totalItems || isExportingToExcel" @click="debounceonClickExportToExcel()">
            <b-icon-download class="mr-2"></b-icon-download> {{ $t("export") }}
          </b-button>
        </div>
      </div>

      <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="onSubmitSearch">
        <b-form-input class="mr-3" type="search" style="width: 280px;"
                      minlength="2" maxlength="200" v-model="searchText"
                      :placeholder= "$t('meetingSearchPlaceholder')" />
        <b-form-select v-if="isUserAdmin()" class="mr-3" :options="searchAreaOptions" v-model="searchAreaId" style="width: 280px;"></b-form-select>
        <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
          <div class="flex-shrink-0 mr-3">{{ $t("fromDate")  }}</div>
          <b-form-datepicker v-model="startDate" value-as-date required
                             :max="new Date()"
                             no-flip hide-header
                             label-no-date-selected="DD/MM/YYYY"
                             locale="vi"
                             :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                             start-weekday="1"></b-form-datepicker>
        </div>
        <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
          <div class="flex-shrink-0 mr-3">{{ $t("toDate")  }}</div>
          <b-form-datepicker v-model="endDate" value-as-date required
                             :max="new Date()"
                             no-flip hide-header
                             label-no-date-selected="DD/MM/YYYY"
                             locale="vi"
                             :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                             start-weekday="1"></b-form-datepicker>
        </div>
        <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
      </b-form>

      <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" :busy="isLoading" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(name)="data">
          <router-link :to="'/customers/details/' + data.item.customerId + '/meetings'">
            {{ data.item.name }}
          </router-link>
        </template>
      </b-table>

      <div class="d-flex flex-row align-items-center justify-content-between">
        <div></div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

    </b-container>
  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import moment from "moment";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import * as reportService from "@/service/reportService";
  import * as areaService from "@/service/areaService";
  import { BounceTimes } from '../enums/TimesNumbers';
 import * as exportService from "@/service/exportService";
 import {formatDateRange}  from "@/service/util";
 import { SearchRequestManager } from "@/utils/search-request-manager";

  @Component
  export default class MeetingCount extends BaseComponent {
    searchManager = new SearchRequestManager();

    searchText = "";

    searchAreaId: number = null;
    searchAreaOptions = [];

    startDate: Date = null;
    endDate: Date = null;
    formErrorMsg = "";
    isLoading = false;

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
      {
        key: "customerId",
        label: "ID",
        thClass: "text-nowrap",
      },
      {
        key: "customerCode",
        label: this.$t("customerIdShort").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "name",
        label: this.$t("customerNameShort").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "areaName",
        label: this.$t("saleArea").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "totalMeetings",
        label: this.$t("totalMeetings").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "successfulMeetings",
        label: this.$t("success").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "meetingsFailed",
        label: this.$t("unsuccess").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "maxParticipant",
        label: this.$t("maxParticipant").toString(),
        thClass: "text-nowrap",
      },
    ];

    isExportingToExcel = false;

    itemsPerPageSelections = [
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
      { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;


    mounted() {
      this.startDate = moment().subtract(30, "days").startOf("day").toDate();
      this.endDate = moment().endOf("day").toDate();
      this.fetchAreas();
    }

    async fetchAreas() {
      try {
        const areas = await areaService.getAll();
        this.searchAreaOptions = areas.map(a => ({ value: a.id, text: a.name }));
        this.searchAreaOptions.unshift({ value: null, text: this.$t("all").toString()});

      } catch (e) {
        this.toastError(e);
      }
    }

    onClickReloadBtn() {
      this.searchManager.reset();
      this.searchText = "";
      this.startDate = moment().subtract(30, "days").startOf("day").toDate();
      this.endDate = moment().endOf("day").toDate();
      this.formErrorMsg = "";
      this.itemsPerPage = 20;
      this.currentPage = 1;
      this.totalItems = 0;

      this.table.refresh();
    }

    debounceonClickSearchBtn = _.debounce(() => {
      this.onClickSearchBtn();
    }, BounceTimes.BOUNCETIMES);

    onClickSearchBtn() {
      this.table.refresh();
    }

    onSubmitSearch() {
      this.searchManager.onSubmit({
        debounceFn: this.debounceonClickSearchBtn,
        resetPage: () => {
          this.currentPage = 1;
        },
        execute: () => {
          this.table.refresh();
        },
      });
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {      
      const currentRequestId = this.searchManager.nextId();
      try {
        const { fromDate, toDate } = formatDateRange(this.startDate, this.endDate);
        const customerListRes = await reportService.getCustomerMeetingsCount({
          fromDate: fromDate,
          toDate: toDate,
          page: ctx.currentPage,
          pageSize: this.itemsPerPage,

          q: this.searchText?.trim(),
          areaId: this.searchAreaId
        });

        if (!this.searchManager.isLatest(currentRequestId)) {
          return [];
        }

        this.totalItems = customerListRes.total;
        return customerListRes.data;

      } catch (e) {
        if (this.searchManager.isLatest(currentRequestId)) {
          this.toastError(e);
        }
        return [];
      }
    }


    debounceonClickExportToExcel  = _.debounce(() => {
    this.onClickExportToExcel ();
    }, BounceTimes.BOUNCETIMES);
    async onClickExportToExcel() {
      try {
        this.isExportingToExcel = true;
        const customers = [];

        while (true) {
          const pageSize = 500;
          const customerListRes = await reportService.getCustomerMeetingsCount({
            fromDate: this.startDate.toISOString(),
            toDate: this.endDate.toISOString(),
            page: 1,
            pageSize,
            q: this.searchText?.trim(),
          });
          const part: any[] = Array.isArray(customerListRes.data)? customerListRes.data : [];
          customers.push(...part);
          if (part.length < pageSize) break;
        }

        const sheetData = customers.map(it => [
          it.customerId, it.customerCode, it.name, it.totalMeetings, it.successfulMeetings, it.meetingsFailed, it.maxParticipant
        ]);
        sheetData.unshift(["ID", "Mã KH", "Tên KH", "Tổng số cuộc họp", "Thành công", "Không thành công", "SL tham dự lớn nhất"]);
        await exportService.exportExcelFile(sheetData, 'ThongKeSoLuongCuocHop', 'TK_SoLuongCH');
      } catch (e) {
        console.error(e);
        this.toastError(e);

      } finally {
        this.isExportingToExcel = false;
      }
    }

  }
</script>
